import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Select } from "@src/common/components";
import { PackageSize } from "@api/graphql/generated/generated-types";
import { displayPackageSize } from "@src/shipments/utils/displayPackageSize";
import { RequiredItemLabel } from "./RequiredItemLabel";
import type { PackageTypeOrSizeMode } from "./packageTypeOrSizeMode";

const PACKAGE_SIZE_OPTIONS = [
  { id: PackageSize.Xs, label: displayPackageSize(PackageSize.Xs) },
  { id: PackageSize.S, label: displayPackageSize(PackageSize.S) },
  { id: PackageSize.M, label: displayPackageSize(PackageSize.M) },
  { id: PackageSize.L, label: displayPackageSize(PackageSize.L) },
  { id: PackageSize.Xl, label: displayPackageSize(PackageSize.Xl) },
  { id: PackageSize.Xxl, label: displayPackageSize(PackageSize.Xxl) },
];

interface PackageItemTypeOrSizeProps {
  mode: PackageTypeOrSizeMode;
  itemPackageTypeIdFieldName: string;
  packageSizeFieldName?: string;
  packageTypes?: Array<{ id: string; name: string }>;
  packageTypesError?: string | null;
  isTypeRequired?: boolean;
  isSizeRequired?: boolean;
  showSize: boolean;
  onModified?: () => void;
}

export function PackageItemTypeOrSize({
  mode,
  itemPackageTypeIdFieldName,
  packageSizeFieldName,
  packageTypes,
  packageTypesError,
  isTypeRequired,
  isSizeRequired,
  showSize,
  onModified,
}: PackageItemTypeOrSizeProps) {
  const { control } = useFormContext();
  if (mode === "none") return null;

  if (mode === "type") {
    return (
      <div className="flex flex-col flex-1 min-w-[10rem]">
        <RequiredItemLabel required={isTypeRequired}>Type</RequiredItemLabel>
        <Controller
          control={control}
          name={itemPackageTypeIdFieldName}
          rules={{
            ...(isTypeRequired ? { required: "Type is required" } : {}),
            validate: () => packageTypesError ?? true,
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <Select
                size="mini"
                backspaceRemoves={false}
                clearable={true}
                deleteRemoves={false}
                escapeClearsValue={false}
                placeholder="None"
                error={!!error}
                options={(packageTypes ?? []).map((type) => ({
                  id: type.id,
                  label: type.name,
                }))}
                value={packageTypeSelectValue(value as string | null, packageTypes)}
                searchable={false}
                onChange={(params) => {
                  const selectedId =
                    params.value.length === 0 ? null : params.value[0].id;
                  onChange(selectedId);
                  onModified?.();
                }}
              />
              {(error?.message || packageTypesError) && (
                <span className="text-xs text-red-500 mt-0.5">
                  {error?.message ?? packageTypesError}
                </span>
              )}
            </>
          )}
        />
      </div>
    );
  }

  if (!showSize || !packageSizeFieldName) return null;

  return (
    <div className="flex flex-col flex-1 min-w-[10rem]">
      <RequiredItemLabel required={isSizeRequired}>Size</RequiredItemLabel>
      <Controller
        control={control}
        name={packageSizeFieldName}
        rules={
          isSizeRequired
            ? {
                validate: (value) =>
                  hasPackageSizeValue(value) || "Size is required",
              }
            : undefined
        }
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Select
              size="mini"
              options={PACKAGE_SIZE_OPTIONS}
              value={packageSizeSelectValue(value)}
              placeholder="None"
              error={!!error}
              onChange={(params) => {
                onChange(
                  params.value.length === 0 ? null : params.value[0],
                );
                onModified?.();
              }}
            />
            {error?.message && (
              <span className="text-xs text-red-500 mt-0.5">
                {error.message}
              </span>
            )}
          </>
        )}
      />
    </div>
  );
}

function packageTypeSelectValue(
  value: string | null | undefined,
  packageTypes?: Array<{ id: string; name: string }>,
) {
  if (!value || !packageTypes) return undefined;
  const selectedPackageType = packageTypes.find((type) => type.id === value);
  if (!selectedPackageType) return undefined;
  return [
    {
      id: selectedPackageType.id,
      label: selectedPackageType.name,
    },
  ];
}

function hasPackageSizeValue(value: unknown): boolean {
  if (value == null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object" && value !== null && "id" in value) {
    return (value as { id?: unknown }).id != null;
  }
  return true;
}

function packageSizeSelectValue(value: unknown) {
  if (!hasPackageSizeValue(value)) return undefined;
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return PACKAGE_SIZE_OPTIONS.filter((option) => option.id === value);
  }
  return [value];
}
